import { yupResolver } from "@hookform/resolvers/yup";
import { CourseHeroContent } from "api/shop";
import { PaziaFormButton, PaziaSwitch, PaziaInput, PaziaSelect, PaziaFileDropzone } from "components/atoms";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { useShopImageUpload } from "./useShopImageUpload";
import { useCourseGroupAttributesAll } from "api/coursegroup-attributes";
import { CourseGroupAttributeType } from "api/coursegroup-attributes/coursegroupAttributesApi";

interface CourseHeroFormProps {
  onSubmit: (data: CourseHeroFormValues) => void;
  onCancel: () => void;
  defaultValues?: CourseHeroContent;
  isLoading?: boolean;
}

const BACKGROUND_MODE_OPTIONS = [
  { value: "course-image", label: "Obraz grupy kursów" },
  { value: "color", label: "Kolor" },
  { value: "image", label: "Własny obraz" },
];

export const CourseHeroForm = ({ onSubmit, onCancel, defaultValues, isLoading }: CourseHeroFormProps) => {
  const { uploadImage } = useShopImageUpload();
  const { allAttributes } = useCourseGroupAttributesAll();

  const selectAttributeOptions = [
    { value: "", label: "— brak —" },
    ...allAttributes
      .filter(
        (a) =>
          a.attributeType === CourseGroupAttributeType.DictionarySingleSelect ||
          a.attributeType === CourseGroupAttributeType.DictionaryMultiSelect,
      )
      .map((a) => ({ value: String(a.id), label: a.name })),
  ];

  const methods = useForm<CourseHeroFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      showBreadcrumbs: defaultValues?.showBreadcrumbs ?? true,
      showCourseNameInBreadcrumbs: defaultValues?.showCourseNameInBreadcrumbs ?? true,
      showTitle: defaultValues?.showTitle ?? true,
      imageHeight: defaultValues?.imageHeight,
      backgroundMode: defaultValues?.backgroundMode ?? "course-image",
      bgColor: defaultValues?.bgColor ?? "",
      bgImage: defaultValues?.bgImage ?? "",
      breadcrumbAttributeId: defaultValues?.breadcrumbAttributeId
        ? String(defaultValues.breadcrumbAttributeId)
        : "",
    },
  });

  const backgroundMode = methods.watch("backgroundMode");

  const handleFormSubmit = async (data: CourseHeroFormValues) => {
    const result: CourseHeroFormValues = {
      showBreadcrumbs: data.showBreadcrumbs,
      showCourseNameInBreadcrumbs: data.showCourseNameInBreadcrumbs,
      showTitle: data.showTitle,
      backgroundMode: data.backgroundMode,
    };
    if (data.imageHeight) {
      result.imageHeight = Number(data.imageHeight);
    }
    if (data.backgroundMode === "color") {
      result.bgColor = data.bgColor;
    }
    if (data.backgroundMode === "image" && data.bgImage) {
      result.bgImage = await uploadImage(data.bgImage);
    }
    if (data.breadcrumbAttributeId) {
      result.breadcrumbAttributeId = Number(data.breadcrumbAttributeId);
    }
    onSubmit(result);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleFormSubmit)} className="grid gap-4">
        <PaziaSwitch name="showBreadcrumbs" label="Pokaż breadcrumbs" />
        <PaziaSwitch name="showCourseNameInBreadcrumbs" label="Pokaż nazwę kursu w breadcrumbs" />
        <PaziaSwitch name="showTitle" label="Pokaż tytuł grupy kursów" />
        <PaziaInput name="imageHeight" label="Wysokość obrazu hero (px)" type="number" placeholder="400" />

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Breadcrumb — kategoria</h4>
          <PaziaSelect
            name="breadcrumbAttributeId"
            label="Atrybut kategorii breadcrumb"
            options={selectAttributeOptions}
          />
        </div>

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Tryb tła</h4>
          <PaziaSelect
            name="backgroundMode"
            label="Tryb tła"
            options={BACKGROUND_MODE_OPTIONS}
          />

          {backgroundMode === "color" && (
            <div className="mt-3">
              <PaziaInput
                name="bgColor"
                label="Kolor tła (np. #1e293b)"
                placeholder="#1e293b"
              />
            </div>
          )}

          {backgroundMode === "image" && (
            <div className="mt-3 [&_img]:max-h-48">
              <PaziaFileDropzone
                name="bgImage"
                label="Własny obraz tła"
                type="image"
                single
                preview="image"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const schema = yup
  .object({
    showBreadcrumbs: yup.boolean().default(true),
    showCourseNameInBreadcrumbs: yup.boolean().default(true),
    showTitle: yup.boolean().default(true),
    imageHeight: yup
      .number()
      .transform((value, original) => (original === "" ? undefined : value))
      .optional()
      .positive("Wysokość musi być dodatnia")
      .integer("Wysokość musi być liczbą całkowitą"),
    backgroundMode: yup
      .string()
      .oneOf(["course-image", "color", "image"])
      .default("course-image"),
    bgColor: yup.string().when("backgroundMode", {
      is: "color",
      then: (s) => s.required("Kolor tła jest wymagany"),
      otherwise: (s) => s.optional(),
    }),
    bgImage: yup.mixed().when("backgroundMode", {
      is: "image",
      then: (s) => s.required("Obraz tła jest wymagany"),
      otherwise: (s) => s.optional(),
    }),
    breadcrumbAttributeId: yup.string().optional(),
  })
  .required();

export interface CourseHeroFormValues {
  showBreadcrumbs?: boolean;
  showCourseNameInBreadcrumbs?: boolean;
  showTitle?: boolean;
  imageHeight?: number;
  backgroundMode?: "course-image" | "color" | "image";
  bgColor?: string;
  bgImage?: string;
  breadcrumbAttributeId?: number | string;
}
